import { apiGet } from "./apiClient";
import { fetchMe } from "./users";

/**
 * Per-customer data scoping for the OSO Command (MANAGER-facing) app, done on
 * the frontend (the shared field-support backend's /kpis/* and /mower-status
 * endpoints are unscoped).
 *
 * A manager should see ALL of their company's devices, so the scope source is
 * the company's device pool: `/users/me/all-devices`, which for an external
 * user returns their `dbo.CustomerDevices` pool (the devices their company
 * owns). This is deliberately DIFFERENT from the mobile app, where a crew
 * member sees only the devices personally assigned to them via
 * `/users/me/devices` (their `dbo.UserDevices` rows).
 *
 * Returns:
 *   - `null`  → caller is internal Oso staff; do NOT scope (see everything).
 *   - `[]`    → external user with no company pool — either not attached to a
 *               company (`customer_id` is NULL) or the company has no
 *               CustomerDevices. They should see nothing (callers must
 *               short-circuit, NOT query unfiltered — an empty device_id
 *               filter would leak the whole fleet).
 *   - `[...]` → the company's device_ids the manager is limited to.
 */
export type DeviceScope = string[] | null;

interface AllDevicesResponse {
  devices: { device_id: string }[];
  total: number;
}

let _scope: Promise<DeviceScope> | null = null;

async function computeScope(): Promise<DeviceScope> {
  const me = await fetchMe();
  if (me.user_type === "internal") return null; // unrestricted
  const res = await apiGet<AllDevicesResponse>("/users/me/all-devices");
  return res.devices.map((d) => d.device_id);
}

/** Resolve the caller's device scope, computed once per session. */
export function getDeviceScope(force = false): Promise<DeviceScope> {
  if (!_scope || force) _scope = computeScope();
  return _scope;
}

/**
 * Turn a scope into the `device_id` query value for the KPI endpoints.
 * `null` (internal) → undefined (no filter). A device list → comma-joined.
 * NOTE: callers must handle an empty external list themselves (render empty),
 * because an empty/undefined filter means "whole fleet" server-side.
 */
export function scopeToDeviceIdParam(scope: DeviceScope): string | undefined {
  if (scope === null) return undefined;
  return scope.join(",");
}
